import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  Placement,
  ReferenceType,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  UseHoverProps,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";
import { Portal } from "@headlessui/react";
import { cx } from "@jugl-web/utils";
import { FC, ReactElement, ReactNode, useRef, useState } from "react";

const tooltipDelayToUseHoverDelay: Record<
  TooltipDelay,
  UseHoverProps["delay"]
> = {
  default: { open: 500 },
  none: undefined,
};

type TooltipDelay = "default" | "none";

export interface RenderTooltipTriggerProps {
  ref: (node: ReferenceType | null) => void;
  props: Record<string, unknown>;
  open: () => void;
  isOpen: boolean;
}

export interface TooltipProps {
  children: ReactNode;
  isOpen?: boolean;
  placement?: Placement;
  delay?: TooltipDelay;
  isDisabled?: boolean;
  /**
   * Keeps the tooltip open while the pointer moves onto its content, so
   * interactive elements (links, buttons) inside `children` stay clickable.
   */
  isInteractive?: boolean;
  className?: string;
  arrowClassName?: string;
  zIndex?: number;
  /**
   * Function to render the trigger element for the tooltip.
   * @warning Do not nest this inside another `renderTrigger` (e.g., Popover's),
   * as it may cause instability and potentially crash the app.
   */
  renderTrigger: (props: RenderTooltipTriggerProps) => ReactElement;
}

// TODO: Revisit and implement according to the latest floating-ui docs
export const Tooltip: FC<TooltipProps> = ({
  children,
  isOpen,
  placement = "bottom",
  delay = "default",
  isDisabled,
  isInteractive,
  className,
  arrowClassName,
  zIndex,
  renderTrigger,
}) => {
  const isControlled = typeof isOpen === "boolean";
  const [isInternallyOpen, setIsInternallyOpen] = useState(false);

  const arrowRef = useRef<SVGSVGElement | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isControlled ? isOpen : isInternallyOpen,
    onOpenChange: isControlled ? undefined : setIsInternallyOpen,
    placement,
    middleware: [
      offset({ mainAxis: 12 }),
      flip(),
      shift({ padding: 12 }),
      arrow({ element: arrowRef, padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const { isMounted, styles } = useTransitionStyles(context);

  const hover = useHover(context, {
    enabled: !isDisabled,
    delay: tooltipDelayToUseHoverDelay[delay],
    move: false,
    handleClose: isInteractive ? safePolygon() : undefined,
  });

  const dismiss = useDismiss(context, {
    enabled: !isDisabled,
    referencePress: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  const referenceProps = getReferenceProps();

  const floatingProps = getFloatingProps({
    onClick: (event) => event.stopPropagation(),
  });

  const openTooltip = () => {
    if (isControlled) {
      return;
    }
    setIsInternallyOpen(true);
  };

  return (
    <>
      {renderTrigger({
        ref: refs.setReference,
        props: referenceProps,
        open: openTooltip,
        isOpen: context.open,
      })}
      {isMounted && !isDisabled && (
        <Portal>
          <div
            ref={refs.setFloating}
            className={cx(
              "z-tooltip max-w-[260px] rounded-lg bg-dark/80 p-2 font-secondary text-sm leading-[20px] text-white",
              className
            )}
            style={{ zIndex, ...floatingStyles, ...styles }}
            {...floatingProps}
          >
            {children}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              className={cx("fill-dark/80", arrowClassName)}
            />
          </div>
        </Portal>
      )}
    </>
  );
};
